import React from 'react';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Flatpickr from "react-flatpickr";
import Select2Wrapper from '../Select2Wrapper';

// MUI transitions
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import { PARTICIPANT_CONFIRMED_STATUSES } from '../../constants/ehc/ParticipantConstants';

export default class EhcParticipantForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      participant: this.initializeParticipant(props.participant, props.mode),
      hospitalOptions: [],
      doctorOptions: [],
      doctorsHospitalId: null,
      confirming: false,
      createError: null,
    };
  }

  isCreateMode = () => (this.props.mode || 'edit') === 'create';

  initializeParticipant = (participant, mode = this.props.mode) => {
    if ((mode || 'edit') === 'create') {
      return {
        covered: true,
        result_enabled: true,
        applied_additions: {},
        ...(participant || {}),
      };
    }
    return { ...(participant || {}) };
  };

  baseUrl = (company_id) => `/api/ehc/companies/${company_id}/participants`;

  getServiceOrder = () => this.props.serviceOrder || (this.state.participant || {}).service_order;

  getAdditionalTests = () => {
    const participant = this.state.participant || {};
    if (participant.available_additional_tests?.length > 0) {
      return participant.available_additional_tests;
    }
    const serviceOrder = this.getServiceOrder();
    return serviceOrder?.additional_tests
      || serviceOrder?.service_package?.additional_tests
      || [];
  };

  // The service date must stay inside the service order window, same as the API enforces.
  getServiceDateLimits = () => {
    const serviceOrder = this.getServiceOrder() || {};
    const limits = {};
    if (serviceOrder.start_date) limits.minDate = new Date(serviceOrder.start_date);
    if (serviceOrder.end_date) limits.maxDate = new Date(serviceOrder.end_date);
    return limits;
  };

  handleChange = (...args) => {
    let newParticipantData = this.state.participant || {};
    for (let i = 0; (i + 1) < args.length; i += 2) {
      const field = args[i];
      const value = args[i + 1];
      newParticipantData[field] = value;
    }
    this.setState({ participant: newParticipantData });
  }

  getErrorMessage = (result) => {
    if (!result) return 'Failed to save participant';
    if (typeof result.errors === 'string') return result.errors;
    if (Array.isArray(result.errors) && result.errors.length > 0) return result.errors.join(', ');
    if (result.errors && typeof result.errors === 'object') {
      return Object.entries(result.errors).map(([key, value]) => `${key}: ${value}`).join(', ');
    }
    if (result.message) return result.message;
    return 'Failed to save participant';
  };

  handleParticipantCreate = async (event) => {
    event.preventDefault();

    const participant = this.state.participant;
    if (!this.props.editable || !participant) return;

    const companyId = this.props.companyId || participant.company_id;
    const serviceOrderId = this.props.serviceOrderId || participant.service_order_id;

    const formData = {
      service_order_id: serviceOrderId,
      profile_full_name: participant.profile_full_name,
      profile_nik: participant.profile_nik,
      profile_email: participant.profile_email,
      profile_date_of_birth: participant.profile_date_of_birth,
      profile_mobile_phone: participant.profile_mobile_phone,
      profile_gender: participant.profile_gender,
      employment_id_number: participant.employment_id_number,
      hospital_id: participant.hospital_id ? +participant.hospital_id : null,
      visit_date: participant.visit_date,
      covered: participant.covered,
      result_enabled: participant.result_enabled,
      employee_position: participant.employee_position,
      employee_division: participant.employee_division,
      applied_additions: participant.applied_additions || {},
    };

    const csrfToken = document.querySelector('[name=csrf-token]').content;
    const url = this.baseUrl(companyId);

    try {
      this.setState({ createError: null });
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        this.onClose();
        if (this.props.tableRef.current) {
          this.props.tableRef.current.onQueryChange();
        }
      } else {
        this.setState({ createError: this.getErrorMessage(result) });
      }
    } catch (err) {
      console.error('Error creating participant:', err);
      this.setState({ createError: err.message || 'Failed to create participant' });
    }
  };

  handleParticipantUpdate = async (event) => {
    event.preventDefault();

    const participant = this.state.participant;
    if (!this.props.editable || !participant) return;

    let formData = {
      profile_full_name: participant.profile_full_name,
      profile_nik: participant.profile_nik,
      hospital_id: +participant.hospital_id,
      visit_date: participant.visit_date,
      employment_id_number: participant.employment_id_number,
      profile_email: participant.profile_email,
      profile_date_of_birth: participant.profile_date_of_birth,
      profile_mobile_phone: participant.profile_mobile_phone,
      profile_gender: participant.profile_gender,
      employee_position: participant.employee_position,
      employee_division: participant.employee_division,
      covered: participant.covered,
      result_enabled: participant.result_enabled,
      applied_additions: participant.applied_additions
    };

    if (PARTICIPANT_CONFIRMED_STATUSES.includes(participant.status)) {
      formData = {
        ...formData,
        doctor_ids: participant.doctor_ids,
        confirmed_datetime: participant.confirmed_datetime,
        notes: participant.notes
      };
    }

    const csrfToken = document.querySelector('[name=csrf-token]').content;
    const url = `${this.baseUrl(participant.company_id)}/${participant.id}`;

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      console.log('Participant update result:', result);

      if (response.ok) {
        this.setState({ createError: null });
        this.onClose();
        if (this.props.tableRef.current) {
          this.props.tableRef.current.onQueryChange();
        }
      } else {
        this.setState({ createError: this.getErrorMessage(result) });
      }
    } catch (err) {
      console.error('Error updating participant:', err);
      this.setState({ createError: err.message || 'Failed to update participant' });
    }
  };

  fetchDoctors = (hospitalId) => {
    return new Promise((resolve, reject) => {
      const url = `/api/doctors${hospitalId ? '?hospital_id=' + hospitalId : ''}`;
      fetch(url)
        .then(async res => {
          if (res.status >= 400) {
            throw new Error(`HTTP error! Status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          resolve(data);
        })
        .catch(error => {
          console.error('Fetch doctors error:', error);
          return null;
        });
    })
  };

  loadDoctorsIfNeeded = async (hospitalId, status) => {
    if (!PARTICIPANT_CONFIRMED_STATUSES.includes(status) || this.state.doctorsHospitalId === hospitalId) return;

    if (!hospitalId) {
      this.setState({ doctorOptions: [], doctorsHospitalId: hospitalId });
      return;
    }

    const doctors = await this.fetchDoctors(hospitalId);
    const doctorOptions = doctors?.map(doctor => ({value: doctor.id, label: doctor.full_name})) || [];
    this.setState({ doctorOptions, doctorsHospitalId: hospitalId });
  };

  loadHospitalsIfNeeded = async (prevProps) => {
    if (this.props.hospitals !== prevProps?.hospitals) {
      this.setState({ hospitalOptions: this.props.hospitals?.map(([name, id]) => ({ value: id, label: name })) || [] });
    }
  };

  onConfirm = () => {
    this.setState({ confirming: true });
  };

  onCancel = () => {
    this.setState({ confirming: false });
  };

  onClose = () => {
    this.onCancel();
    this.setState({ createError: null });
    this.props.onClose();
  };

  // =====================
  // Render Component
  // =====================
  async componentDidMount() {
    await this.loadDoctorsIfNeeded(this.state.participant?.hospital_id, this.state.participant?.status);
    await this.loadHospitalsIfNeeded();
  }

  async componentDidUpdate(prevProps, prevState) {
    await this.loadDoctorsIfNeeded(this.state.participant?.hospital_id, this.state.participant?.status);
    await this.loadHospitalsIfNeeded(prevProps);

    // If participant prop changes
    if (this.props.participant !== prevProps.participant) {
      this.setState({ participant: this.initializeParticipant(this.props.participant), createError: null });
    }
  }

  // Confirmed participants carry extra visit data, which is what used to push the
  // modal past the fold. Fields stay in three columns so the form still fits a
  // laptop viewport without an inner scrollbar.
  renderFooter = (isCreate) => {
    if (isCreate) {
      return (
        <>
          <Button variant="outlined" onClick={this.onClose}>
            Cancel
          </Button>
          {this.props.editable &&
            <Button
              variant="contained"
              color="primary"
              disabled={!this.state.participant}
              className="ml-2"
              type="submit"
            >
              Save
            </Button>
          }
        </>
      );
    }

    if (this.props.editable && this.state.confirming) {
      return (
        <>
          <span className="form-modal__prompt">Apply these changes to the participant?</span>
          <Button variant="outlined" onClick={this.onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!this.state.participant}
            className="ml-2"
            type="submit"
          >
            Confirm Update
          </Button>
        </>
      );
    }

    return (
      <>
        {this.props.removable &&
          <Button
            variant="outlined"
            onClick={this.props.onRemove}
            disabled={!this.state.participant}
            className="form-modal__remove mr-auto"
          >
            Remove
          </Button>
        }
        <Button variant="outlined" onClick={this.onClose}>
          Cancel
        </Button>
        {this.props.editable &&
          <Button
            variant="contained"
            color="primary"
            disabled={!this.state.participant}
            className="ml-2"
            onClick={this.onConfirm}
          >
            Update
          </Button>
        }
      </>
    );
  };

  render() {
    const isCreate = this.isCreateMode();
    const additionalTests = this.getAdditionalTests();
    const appliedAdditions = this.state.participant?.applied_additions || {};
    const participant = this.state.participant;
    const isConfirmed = !isCreate && PARTICIPANT_CONFIRMED_STATUSES.includes(participant?.status);
    const requiredWhenConfirmed = isConfirmed ? { required: "required" } : {};
    const col = "col-md-6 col-xl-4";

    return (
      <Modal
        open={this.props.open}
        onClose={this.onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        style={{ overflow: 'auto' }}
      >
        <Fade in={this.props.open} timeout={500}>
          <Box className="form-modal">
            <form onSubmit={isCreate ? this.handleParticipantCreate : this.handleParticipantUpdate}>
              <div className="form-modal__head">
                <Typography variant="h6" className="mb-0">
                  {isCreate ? (this.props.addTitle || 'Add Participant') : (this.props.editTitle || 'Participant Data')}
                </Typography>
                {!isCreate && participant?.status &&
                  <span className="form-modal__status">{participant.status.replace(/_/g, ' ')}</span>
                }
              </div>

              <div className="form-modal__body">
                {this.state.createError &&
                  <div className="alert alert-danger" role="alert">
                    {this.state.createError}
                  </div>
                }

                { participant &&
                  <>
                    <section className="form-modal__section">
                      <h6 className="form-modal__section-title">{this.props.labels?.section_identity || 'Data peserta'}</h6>
                      <div className="row">
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="profile_full_name">Name *</label>
                            <input
                              placeholder="Input full name"
                              disabled={!this.props.editable}
                              className="form-control"
                              required="required"
                              type="text"
                              value={participant.profile_full_name || ''}
                              name="profile_full_name"
                              onChange={(e) => this.handleChange("profile_full_name", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="profile_nik">NIK *</label>
                            <input
                              placeholder="Input NIK"
                              disabled={!this.props.editable}
                              className="form-control"
                              required="required"
                              type="text" pattern="[0-9]{16}" maxLength={16}
                              title="NIK must be 16 digits"
                              value={participant.profile_nik || ''}
                              name="profile_nik"
                              onChange={(e) => this.handleChange("profile_nik", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="profile_date_of_birth">DOB *</label>
                            <Flatpickr
                              placeholder="YYYY-MM-DD"
                              disabled={!this.props.editable}
                              className="form-control"
                              required="required"
                              pattern="\d{4}-\d{2}-\d{2}"
                              title="Date must be in the format YYYY-MM-DD"
                              value={participant.profile_date_of_birth || ''}
                              onClose={(selectedDates, dateStr, instance) => {
                                this.handleChange("profile_date_of_birth", dateStr);
                              }}
                              options={{
                                dateFormat: "Y-m-d",
                                allowInput: true
                              }}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="profile_gender">Gender</label>
                            <select
                              disabled={!this.props.editable}
                              className="form-control"
                              value={participant.profile_gender || ''}
                              name="profile_gender"
                              onChange={(e) => this.handleChange("profile_gender", e.target.value)}
                            >
                              <option value="">Select gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                            <i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="profile_email">Email *</label>
                            <input
                              placeholder="Input email address"
                              disabled={!this.props.editable}
                              className="form-control"
                              required="required"
                              type="email"
                              value={participant.profile_email || ''}
                              name="profile_email"
                              onChange={(e) => this.handleChange("profile_email", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="profile_mobile_phone">Phone *</label>
                            <input
                              placeholder="08XX XXXX XXXX"
                              disabled={!this.props.editable}
                              className="form-control"
                              required="required"
                              type="tel"
                              value={participant.profile_mobile_phone || ''}
                              name="profile_mobile_phone"
                              onChange={(e) => this.handleChange("profile_mobile_phone", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="form-modal__section">
                      <h6 className="form-modal__section-title">{this.props.labels?.section_employment || 'Data kepegawaian'}</h6>
                      <div className="row">
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="employment_id_number">ID *</label>
                            <input
                              placeholder="Input employment ID number"
                              disabled={!this.props.editable}
                              className="form-control"
                              required="required"
                              type="text"
                              value={participant.employment_id_number || ''}
                              name="employment_id_number"
                              onChange={(e) => this.handleChange("employment_id_number", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="employee_position">Position</label>
                            <input
                              placeholder="Input position"
                              disabled={!this.props.editable}
                              className="form-control"
                              type="text"
                              value={participant.employee_position || ''}
                              name="employee_position"
                              onChange={(e) => this.handleChange("employee_position", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="employee_division">Division</label>
                            <input
                              placeholder="Input division"
                              disabled={!this.props.editable}
                              className="form-control"
                              type="text"
                              value={participant.employee_division || ''}
                              name="employee_division"
                              onChange={(e) => this.handleChange("employee_division", e.target.value)}
                            /><i className="form-group__bar"></i>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="form-modal__section">
                      <h6 className="form-modal__section-title">
                        {this.props.labels?.section_scheduling || 'Penjadwalan'}
                        {isConfirmed &&
                          <span className="form-modal__section-hint">
                            {this.props.labels?.scheduling_required_hint || 'wajib diisi bila peserta ditandai Terjadwal'}
                          </span>
                        }
                      </h6>
                      <div className="row">
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="hospital_id">Hospital {isConfirmed ? '*' : ''}</label>
                            <Select2Wrapper
                              {...requiredWhenConfirmed}
                              className="form-control"
                              value={participant.hospital_id?.toString() || ''}
                              name="hospital_id"
                              onChange={(e) => this.handleChange("doctor_ids", [], "hospital_id", +e.target.value)}
                              useDirectParent={true}
                              options={{
                                placeholder: "Search Hospital Branch",
                                disabled: !this.props.editable,
                                allowClear: true
                              }}
                              items={this.state.hospitalOptions}
                            >
                            </Select2Wrapper><i className="form-group__bar"></i>
                          </div>
                        </div>
                        <div className={col}>
                          <div className="form-group">
                            <label htmlFor="visit_date">Service Date {isConfirmed ? '*' : ''}</label>
                            <Flatpickr
                              {...requiredWhenConfirmed}
                              placeholder="YYYY-MM-DD"
                              disabled={!this.props.editable}
                              className="form-control"
                              pattern="\d{4}-\d{2}-\d{2}"
                              title="Date must be in the format YYYY-MM-DD"
                              value={participant.visit_date || ''}
                              onClose={(selectedDates, dateStr, instance) => {
                                this.handleChange("visit_date", dateStr);
                              }}
                              options={{
                                dateFormat: "Y-m-d",
                                allowInput: true,
                                ...this.getServiceDateLimits()
                              }}
                            />
                            <i className="form-group__bar"></i>
                          </div>
                        </div>
                        {isConfirmed &&
                          <>
                            <div className={col}>
                              <div className="form-group">
                                <label htmlFor="confirmed_datetime">Confirmed Datetime *</label>
                                <Flatpickr
                                  required
                                  placeholder="YYYY-MM-DD HH:mm"
                                  disabled={!this.props.editable}
                                  className="form-control"
                                  title="Date must be in the format YYYY-MM-DD HH:mm"
                                  value={participant?.confirmed_datetime || ''}
                                  onClose={([selectedDate], dateStr, instance) => {
                                    this.handleChange("confirmed_datetime", selectedDate.toISOString());
                                  }}
                                  options={{
                                    defaultHour: 0,
                                    dateFormat: "Y-m-d H:i",
                                    enableTime: true,
                                    time_24hr: true,
                                    minuteIncrement: 1,
                                    allowInput: true
                                  }}
                                />
                                <i className="form-group__bar"></i>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="form-group">
                                <label htmlFor="doctor_ids">Doctors *</label>
                                <Select2Wrapper
                                  required
                                  className="form-control"
                                  value={participant?.doctor_ids?.map(doctorId => doctorId.toString()) || []}
                                  name="doctor_ids"
                                  onChange={(e) => this.handleChange("doctor_ids", e.target.selectedOptions ? Array.from(e.target.selectedOptions, option => +option.value) : [])}
                                  useDirectParent={true}
                                  options={{
                                    multiple: true,
                                    disabled: !this.props.editable,
                                    allowClear: true,
                                    closeOnSelect: false,
                                    placeholder: "Search Doctors",
                                  }}
                                  items={this.state.doctorOptions}
                                >
                                </Select2Wrapper><i className="form-group__bar"></i>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="form-group">
                                <label htmlFor="notes">Notes</label>
                                <textarea
                                  placeholder="Input notes"
                                  disabled={!this.props.editable}
                                  className="form-control form-modal__notes"
                                  value={participant.notes || ''}
                                  name="notes"
                                  onChange={(e) => this.handleChange("notes", e.target.value)}
                                /><i className="form-group__bar"></i>
                              </div>
                            </div>
                          </>
                        }
                      </div>
                    </section>

                    <section className="form-modal__section">
                      <h6 className="form-modal__section-title">{this.props.labels?.section_options || 'Opsi'}</h6>
                      <div className="form-modal__checkboxes">
                        <div className="checkbox">
                          <input
                            disabled={!this.props.editable}
                            type="checkbox"
                            checked={participant.covered || false}
                            name="covered"
                            id="covered"
                            onChange={(e) => this.handleChange("covered", e.target.checked)}
                          />
                          <label className="checkbox__label" htmlFor="covered">Covered</label>
                        </div>
                        <div className="checkbox">
                          <input
                            disabled={!this.props.editable}
                            type="checkbox"
                            checked={participant.result_enabled || false}
                            name="result_enabled"
                            id="result_enabled"
                            onChange={(e) => this.handleChange("result_enabled", e.target.checked)}
                          />
                          <label className="checkbox__label" htmlFor="result_enabled">Show Result</label>
                        </div>
                        {[...additionalTests].sort((a, b) => a.id - b.id).map((test) => (
                          <div key={test.id} className="checkbox">
                            <input
                              disabled={!this.props.editable}
                              type="checkbox"
                              checked={appliedAdditions[test.id] || false}
                              name={`applied_additions_${test.id}`}
                              id={`applied_additions_${test.id}`}
                              onChange={(e) => {
                                let nextAdditions = { ...appliedAdditions };
                                nextAdditions[test.id] = e.target.checked;
                                this.handleChange("applied_additions", nextAdditions);
                              }}
                            />
                            <label className="checkbox__label" htmlFor={`applied_additions_${test.id}`}>{test.name}</label>
                          </div>
                        ))}
                      </div>
                    </section>
                  </>
                }
              </div>

              <div className="form-modal__foot">
                {this.renderFooter(isCreate)}
              </div>
            </form>
          </Box>
        </Fade>
      </Modal>
    );
  }
}
